import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, QrCode } from 'lucide-react';
import { qrcodeService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { CompanyQrcode, QrcodeCoupon, QrcodeFilters } from '../types/qrcode';
import { unformatDocument } from '../types/api';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import QrcodeList from '../components/qrcodes/QrcodeList';
import QrcodeForm from '../components/qrcodes/QrcodeForm';
import QrcodeCouponList from '../components/qrcodes/QrcodeCouponList';
import QrcodeCouponForm from '../components/qrcodes/QrcodeCouponForm';
import QrcodeFilters from '../components/qrcodes/QrcodeFilters';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';

type ModalType = 'create' | 'edit' | 'coupons' | 'addCoupon' | 'editCoupon' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

const DEFAULT_LIMIT = 10;

function QrCodes() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingQrcode, setEditingQrcode] = useState<CompanyQrcode | null>(null);
  const [editingCoupon, setEditingCoupon] = useState<QrcodeCoupon | null>(null);
  const [filters, setFilters] = useState<QrcodeFilters>({
    page: 1,
    limit: DEFAULT_LIMIT,
  });
  const queryClient = useQueryClient();

  // Queries
  const { data: qrcodeResponse, isLoading: isLoadingQrcodes } = useQuery({
    queryKey: ['qrcodes', filters],
    queryFn: () => qrcodeService.getQrcodes(filters),
    keepPreviousData: true,
  });

  const { data: promotionCompanies } = useQuery({
    queryKey: ['promotion-companies'],
    queryFn: () => qrcodeService.getPromotionCompanies(),
  });

  const { data: coupons, isLoading: isLoadingCoupons } = useQuery({
    queryKey: ['qrcode-coupons', editingQrcode?.id],
    queryFn: () => qrcodeService.getQrcodeCoupons(editingQrcode?.id),
    enabled: !!editingQrcode && modal.type === 'coupons',
  });

  // Mutations
  const createQrcodeMutation = useMutation({
    mutationFn: (data: Partial<CompanyQrcode>) => qrcodeService.createQrcode(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qrcodes'] });
      messageHandler.success('QR Code criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateQrcodeMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CompanyQrcode> }) =>
      qrcodeService.updateQrcode(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qrcodes'] });
      messageHandler.success('QR Code atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteQrcodeMutation = useMutation({
    mutationFn: (id: number) => qrcodeService.deleteQrcode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qrcodes'] });
      messageHandler.success('QR Code excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const createCouponMutation = useMutation({
    mutationFn: (data: Partial<QrcodeCoupon>) => qrcodeService.createQrcodeCoupon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qrcode-coupons'] });
      messageHandler.success('Cupom criado com sucesso!');
      setModal({ type: 'coupons' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateCouponMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<QrcodeCoupon> }) =>
      qrcodeService.updateQrcodeCoupon(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qrcode-coupons'] });
      messageHandler.success('Cupom atualizado com sucesso!');
      setModal({ type: 'coupons' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteCouponMutation = useMutation({
    mutationFn: (id: number) => qrcodeService.deleteQrcodeCoupon(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qrcode-coupons'] });
      messageHandler.success('Cupom excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleQrcodeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      promotion_company_id: Number(formData.get('promotion_company_id')),
      qr_date: formData.get('qr_date') as string,
      coupon_quantity: Number(formData.get('coupon_quantity')),
      send: formData.get('send') === 'on',
    };

    if (editingQrcode) {
      updateQrcodeMutation.mutate({ id: editingQrcode.id, data });
    } else {
      createQrcodeMutation.mutate(data);
    }
  };

  const handleCouponSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      company_qrcode_id: editingQrcode?.id,
      phone: unformatDocument(formData.get('phone') as string),
      processed: formData.get('processed') === 'on',
    };

    if (editingCoupon) {
      updateCouponMutation.mutate({ id: editingCoupon.id, data });
    } else {
      createCouponMutation.mutate(data);
    }
  };

  const handleDeleteQrcode = (qrcode: CompanyQrcode) => {
    messageHandler.confirm(
      'Excluir QR Code',
      'Tem certeza que deseja excluir este QR Code? Esta ação não pode ser desfeita.',
      () => deleteQrcodeMutation.mutate(qrcode.id)
    );
  };

  const handleDeleteCoupon = (coupon: QrcodeCoupon) => {
    messageHandler.confirm(
      'Excluir Cupom',
      'Tem certeza que deseja excluir este cupom? Esta ação não pode ser desfeita.',
      () => deleteCouponMutation.mutate(coupon.id)
    );
  };

  const handleFilterChange = (newFilters: QrcodeFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: DEFAULT_LIMIT,
    });
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingQrcode(null);
    setEditingCoupon(null);
  };

  const qrcodes = qrcodeResponse?.data || [];
  const pagination = qrcodeResponse?.pagination;
  const totalRecords = pagination?.total || 0;
  const isFiltered = Object.keys(filters).some(
    key => key !== 'page' && key !== 'limit' && filters[key as keyof QrcodeFilters]
  );

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="QR Codes"
          description="Gerencie os QR Codes das empresas participantes."
          actions={
            <Button
              onClick={() => {
                setEditingQrcode(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo QR Code
            </Button>
          }
        />

        <QrcodeFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
        />

        {/* Total Records Count */}
        <div className="mb-4 flex items-center text-sm text-gray-600 dark:text-gray-400">
          <QrCode className="h-4 w-4 mr-2" />
          <span>
            Total de registros: <strong>{totalRecords}</strong>
          </span>
        </div>

        {isLoadingQrcodes ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : qrcodes.length > 0 ? (
          <>
            <QrcodeList
              qrcodes={qrcodes}
              onEdit={qrcode => {
                setEditingQrcode(qrcode);
                setModal({ type: 'edit' });
              }}
              onDelete={handleDeleteQrcode}
              onManageCoupons={qrcode => {
                setEditingQrcode(qrcode);
                setModal({ type: 'coupons' });
              }}
            />

            {pagination && pagination.last_page > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.last_page}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon={<QrCode className="h-12 w-12" />}
            title={isFiltered ? 'Nenhum QR Code encontrado' : 'Nenhum QR Code cadastrado'}
            description={
              isFiltered
                ? 'Tente ajustar os filtros para encontrar o que procura'
                : 'Crie seu primeiro QR Code para começar'
            }
            action={{
              label: 'Criar QR Code',
              onClick: () => {
                setEditingQrcode(null);
                setModal({ type: 'create' });
              },
            }}
          />
        )}

        {/* Create/Edit QR Code Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingQrcode ? 'Editar QR Code' : 'Novo QR Code'}
        >
          <QrcodeForm
            qrcode={editingQrcode}
            promotionCompanies={promotionCompanies || []}
            onSubmit={handleQrcodeSubmit}
            isLoading={createQrcodeMutation.isPending || updateQrcodeMutation.isPending}
            onClose={closeModal}
          />
        </Modal>

        {/* Coupons Modal */}
        <Modal
          isOpen={modal.type === 'coupons'}
          onClose={closeModal}
          title={`Cupons - ${editingQrcode?.company_name || 'QR Code'}`}
          size="lg"
        >
          <div className="mb-4">
            <Button
              onClick={() => {
                setEditingCoupon(null);
                setModal({ type: 'addCoupon' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Cupom
            </Button>
          </div>

          <QrcodeCouponList
            coupons={coupons || []}
            onEdit={coupon => {
              setEditingCoupon(coupon);
              setModal({ type: 'editCoupon' });
            }}
            onDelete={handleDeleteCoupon}
            isLoading={isLoadingCoupons}
          />
        </Modal>

        {/* Create/Edit Coupon Modal */}
        <Modal
          isOpen={modal.type === 'addCoupon' || modal.type === 'editCoupon'}
          onClose={() => setModal({ type: 'coupons' })}
          title={editingCoupon ? 'Editar Cupom' : 'Novo Cupom'}
        >
          <QrcodeCouponForm
            coupon={editingCoupon}
            onSubmit={handleCouponSubmit}
            isLoading={createCouponMutation.isPending || updateCouponMutation.isPending}
            onClose={() => setModal({ type: 'coupons' })}
          />
        </Modal>
      </div>
    </div>
  );
}

export default QrCodes;
